import { useEffect, useRef, type RefObject } from "react";

/**
 * Keep the newest message in view — without taking the scrollbar away.
 *
 * ── THE TWO DEFECTS THIS REPLACES ────────────────────────────────────────────
 *
 * Bot Mode's canvas had no scroll handling at all: a thread longer than the
 * pane simply ran off the bottom and the newest turn — the reply the person was
 * waiting for — was below the fold with nothing to say so.
 *
 * `chat-messages.tsx` had the opposite defect. It set
 * `node.scrollTop = node.scrollHeight` on every message-count change with no
 * check of where the reader was, so scrolling up to re-read something was
 * interrupted by the next arrival, every time.
 *
 * Copying chat's version into Bot Mode would have shipped the second defect to
 * a second screen. Both lists take this instead, so there is one answer rather
 * than two wrong ones.
 *
 * ── HOW "IS THE READER AT THE BOTTOM" IS DECIDED ─────────────────────────────
 *
 * It is decided from the reader's own scrolling, recorded as it happens, and
 * NOT from the geometry after the list has already grown: once a message is
 * appended the distance-from-bottom has already changed, so measuring then
 * would answer a question about the new list, not about where the person was.
 *
 * The threshold exists because "at the bottom" is a human fact, not an integer
 * one. Fractional device-pixel ratios leave `scrollHeight - scrollTop -
 * clientHeight` a pixel or two above zero at rest; an equality test would
 * un-stick a list that is visibly at the bottom and never re-attach.
 */
const NEAR_BOTTOM_PX = 32;

export type MessageListPin = "bottom" | "top";

export type MessageListAutoscrollOptions = {
  /**
   * `"bottom"` (default) follows new messages while the reader is at the
   * bottom. `"top"` is the deliberate opposite — a list whose interesting end
   * is its first row — and it always wins over the reader's position, because
   * a caller only asks for it when the list has just become a different list.
   */
  pin?: MessageListPin;
};

/**
 * @param ref     the scrolling container
 * @param signal  the values whose change means "the list changed". Compared by
 *                `Object.is`, element-wise; a re-render that changes none of
 *                them does not move the reader. Passed as a plain array rather
 *                than as an effect dependency list so a caller may vary its
 *                length between renders without React's dep-array rule.
 */
export function useMessageListAutoscroll(
  ref: RefObject<HTMLElement | null>,
  signal: readonly unknown[],
  options: MessageListAutoscrollOptions = {},
): void {
  const pin = options.pin ?? "bottom";
  const atBottom = useRef(true);
  const previous = useRef<readonly unknown[] | null>(null);

  // No dependency array here either, and that is load-bearing rather than
  // lazy. `[ref]` would attach exactly once, on mount — and a list does not
  // always exist then: `chat-messages.tsx` returns null while it has nothing to
  // show, so its container appears on a LATER render. Attaching once would mean
  // never attaching for that list, `atBottom` would stay true forever, and chat
  // would quietly keep the yanking behaviour this hook exists to end. Detaching
  // and re-attaching per render is cheap for a list that renders when messages
  // arrive, and it is correct when the node appears late or is replaced.
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const onScroll = () => {
      atBottom.current =
        node.scrollHeight - node.scrollTop - node.clientHeight <= NEAR_BOTTOM_PX;
    };
    node.addEventListener("scroll", onScroll, { passive: true });
    return () => node.removeEventListener("scroll", onScroll);
  });

  // No dependency array: the change test is explicit above, and an effect that
  // declared `signal` would be a dep list whose LENGTH can vary by caller.
  useEffect(() => {
    const before = previous.current;
    const changed =
      before === null ||
      before.length !== signal.length ||
      signal.some((value, index) => !Object.is(value, before[index]));
    previous.current = signal;
    if (!changed) return;
    const node = ref.current;
    if (!node) return;
    if (pin === "top") {
      node.scrollTop = 0;
      return;
    }
    if (!atBottom.current) return;
    node.scrollTop = node.scrollHeight;
  });
}
